import styled, { keyframes } from "styled-components";

const bubbleBase = `
  padding: 10px 14px;
  font-size: 15px;
  max-width: calc(100% - 40px);
  word-break: break-word;
  border-radius: 12px;
  transition: all 0.2s ease;
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  max-width: 100%;
  background-color: ${({ theme }) => theme.bodyBg};
  color: ${({ theme }) => theme.text};
  transition: background-color 0.4s ease, color 0.4s ease;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background-color: ${({ theme }) => theme.headerBg};
  color: ${({ theme }) => theme.headerText || theme.text};

  img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 3px solid #ddd;
    margin-right: 16px;
  }

  div {
    display: flex;
    flex-direction: column;
    justify-content: center;

    strong {
      font-size: 16px;
      font-weight: 600;
      color: ${({ theme }) => theme.headerText || theme.text};
    }

    span {
      font-size: 14px;
      color: #888;
      margin-top: 2px;
    }
  }

  @media (max-width: 600px) {
    padding: 12px 16px;

    img {
      width: 40px;
      height: 40px;
      margin-right: 12px;
    }

    div {
      strong {
        font-size: 15px;
      }

      span {
        font-size: 13px;
      }
    }
  }
`;

export const Body = styled.div`
  flex: 1;
  padding: 20px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  background: ${({ theme }) => theme.background};

  @media (max-width: 600px) {
    padding: 12px 12px;
  }
`;

export const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) =>
    align === "right" ? "flex-end" : "flex-start"};
  margin: ${({ $grouped }) => ($grouped ? "2px 12px" : "12px 16px")};
`;

// const sharedBubbleStyle = `
//   border-radius: 12px;
//   padding: 8px 12px;
//   max-width: 65%;
//   word-break: break-word;
//   font-size: 14px;
//   position: relative;
//   box-shadow: 0 1px 3px rgba(0,0,0,0.1);
// `;

export const BotMsg = styled.div`
  ${bubbleBase}
  background-color: ${({ theme }) => theme.bubbleSystem};
  color: ${({ theme }) => theme.text};
  align-self: flex-start;
  text-align: start;
  border-radius: ${({ $grouped }) =>
    $grouped ? "0 8px 12px 12px" : "0 12px 12px 12px"};
`;

export const AgentMsg = styled.div`
  ${bubbleBase}
  background-color: ${({ theme }) => theme.bubbleAgent};
  color: ${({ theme }) => theme.text};
  border-radius: ${({ $grouped }) =>
    $grouped ? "0 8px 12px 12px" : "0 12px 12px 12px"};
`;

export const UserMsg = styled.div`
  ${bubbleBase}
  background-color: ${({ theme }) => theme.bubbleUser};
  color: ${({ theme }) => theme.text};
  border-radius: ${({ $grouped }) =>
    $grouped ? "8px 0 12px 12px" : "12px 0 12px 12px"};
`;

export const Time = styled.div`
  font-size: 11px;
  color: #000;
  margin-top: 6px;
  text-align: right;
  opacity: 0.7;

  @media (max-width: 600px) {
    font-size: 10px;
  }
`;

export const Sender = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 4px;
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 600px) {
    font-size: 13px;
  }
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.inputBg};
  border-top: 1px solid #ccc;
  position: relative;
  flex-wrap: nowrap;

  @media (max-width: 600px) {
    padding: 10px 12px;
    gap: 6px;
  }
`;

export const FooterLeft = styled.div`
  display: flex;
  gap: 12px;
  margin-right: 12px;

  button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;

    &:first-child {
      color: #e63946;
    }

    &:last-child {
      color: ${({ theme }) => theme.primary || "#ff3d03"};
    }
  }

  @media (max-width: 600px) {
    gap: 8px;

    button {
      font-size: 22px;
    }
  }
`;

export const ActionIcons = styled.div`
  position: absolute;
  left: 16px;
  top: -72px;
  background: ${({ theme }) => theme.headerBg};
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  button {
    background: none;
    border: none;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    color: ${({ theme }) => theme.text};
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

export const Input = styled.input`
  flex: 1;
  padding: 10px 16px;
  background: ${({ theme }) => theme.inputBg};
  border: 2px solid #fff;
  border-radius: 16px;
  font-size: 15px;
  color: ${({ theme }) => theme.text};
  outline: none;
  transition: box-shadow 0.2s ease-in-out, background 0.2s ease-in-out;

  &::placeholder {
    color: ${({ theme }) => theme.placeholderText || "#999"};
  }

  ${(props) =>
    props.$isTyping &&
    `
    box-shadow: 0 0 0 3px #aac4f6;
    background: #eaf0ff;
  `}

  @media (max-width: 600px) {
    font-size: 14px;
    padding: 10px 12px;
  }
`;

export const Button = styled.button`
  background: ${({ theme }) => theme.primary || "#ff3d03"};
  color: ${({ theme }) => theme.buttonText || "#fff"};
  font-weight: bold;
  border: none;
  padding: 10px 18px;
  border-radius: 10px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.3s ease, transform 0.2s ease;

  span.show-on-mobile {
    display: none;
  }

  span.hide-on-mobile {
    display: inline;
  }

  @media (max-width: 600px) {
    padding: 8px 10px;
    font-size: 0;

    span.hide-on-mobile {
      display: none;
    }

    span.show-on-mobile {
      display: inline;
      font-size: 20px;
    }
  }
`;

export const DisabledNotice = styled.p`
  color: #888;
  font-size: 14px;
  margin-left: 12px;
`;

export const ConfigDropdown = styled.div`
  background: ${({ theme }) => theme.headerBg};
  border-radius: 10px;
  box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 12px;
  width: 180px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 18px;
    border-width: 8px;
    border-style: solid;
    border-color: ${({ theme }) => theme.headerBg} transparent transparent transparent;
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
  }

  button {
    background: none;
    border: none;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    color: ${({ theme }) => theme.text} !important;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      opacity: 0.85;
    }
  }

  @media (max-width: 600px) {
    width: 160px;

    button {
      font-size: 13px;
    }
  }
`;

export const Warning = styled.div`
  background: #ffeaea;
  color: #a30000;
  border: 1px solid #ffcccc;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
`;

export const LoaderWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: ${({ theme }) => theme.text};
  font-weight: 500;
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  border: 6px solid rgba(0, 0, 0, 0.1);
  border-left-color: ${({ theme }) => theme.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${rotate} 0.8s linear infinite;
  margin-bottom: 1rem;
`;